import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { config } from '../utils/config';
import { extractDeviceType } from '../utils/device';
import { useAuth } from '../AuthContext';
import SISIcon from '@/assets/device/SIS.svg';
import RAIcon from '@/assets/device/RA.svg';
import ALCIcon from '@/assets/device/ALC.svg';
import OWIcon from '@/assets/device/OW.svg';
import TIIcon from '@/assets/device/TI.svg';
import { DateTime } from 'luxon';
import { expandAxisAlerts, getAxisLabel } from '../utils/alertStatus';

const PRESENT = {
    All: {
        label: '所有',
        bg: '#ffffff',
        ring: '#d1d5db',
        text: '#475569',
    },
    green: {
        label: '正常',
        dot: '🟢',
        bg: '#e6f4ea',
        ring: '#6dc591',
        text: '#166534',
    },
    yellow: {
        label: '逾越警戒值',
        dot: '🟡',
        bg: '#fff8e6',
        ring: '#facc15',
        text: '#92400e',
    },
    red: {
        label: '逾越行動值',
        dot: '🔴',
        bg: '#feeceb',
        ring: '#f87171',
        text: '#b91c1c',
    },
    gray: {
        label: '離線',
        dot: '⚪',
        bg: '#f1f5f9',
        ring: '#cbd5f5',
        text: '#475569',
    },
};

const SECTION_CONFIG = [
    {
        key: 'yellow',
        title: '逾越警戒值',
        description: '逾越警戒值，請密切留意趨勢變化',
        empty: '目前沒有警戒中的設備',
    },
    {
        key: 'red',
        title: '逾越行動值',
        description: '逾越行動值，建議立即啟動處置或派員確認',
        empty: '目前沒有行動層級的警報',
    },
    {
        key: 'gray',
        title: '裝置離線',
        description: '設備離線或超過 12 小時未回傳資料',
        empty: '目前沒有離線設備',
    },
];

const DISABLED_SECTION_DEFAULT = {
    title: '關閉XML/關閉警戒列表',
    description: '設備 XML 回傳或警戒偵測已關閉',
};
const DISABLED_SECTION_ZOO = {
    title: '關閉警戒列表',
    description: '設備警戒偵測已關閉',
};

const THRESHOLD_FLAG_KEYS = [
    'enable_threshold_detect',
    'enable_threshold_detection',
    'threshold_detect_enabled',
];

const XML_FLAG_KEYS = [
    'enable_xml',
    'xml_enable',
    'xml_enabled',
    'enable_xml_upload',
    'enable_xml_collect',
];

const isExplicitFalse = (value) =>
    value === false ||
    value === 0 ||
    value === '0' ||
    value === 'false' ||
    value === 'FALSE';

const hasDisabledFlag = (alert, keys) =>
    keys.some((key) => isExplicitFalse(alert?.[key]));

const getDisableInfo = (alert, { ignoreXml } = {}) => {
    const thresholdDisabled = hasDisabledFlag(alert, THRESHOLD_FLAG_KEYS);
    const xmlDisabled = ignoreXml ? false : hasDisabledFlag(alert, XML_FLAG_KEYS);
    return {
        isDisabled: thresholdDisabled || xmlDisabled,
        thresholdDisabled,
        xmlDisabled,
    };
};

const buildDisabledReason = (info) => {
    const reasons = [];
    if (info.thresholdDisabled) reasons.push('警戒偵測已關閉');
    if (info.xmlDisabled) reasons.push('XML 回傳已關閉');
    return reasons.length ? reasons.join(' / ') : '設定已停用';
};

const deviceTypeIcons = {
    SIS: SISIcon,
    RA: RAIcon,
    ALC: ALCIcon,
    OW: OWIcon,
    TI: TIIcon,
};

const DEVICE_TYPE_ORDER = ['SIS', 'RA', 'ALC', 'OW', 'TI'];

const getAlertDistrict = (alert) =>
    alert?.district ||
    alert?.slope_district ||
    alert?.slopeDistrict ||
    alert?.slope?.district ||
    alert?.slope?.segment ||
    alert?.segment ||
    '未分區';

export default function DeviceStatusBoard() {
    const { user } = useAuth();
    const { alertData = [], slopeData } = useOutletContext();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const styles = useMemo(() => createStyles(isMobile), [isMobile]);
    const ignoreXml = config?.appType === 'zoo';
    const deviceTypeIndex = useMemo(
        () => buildDeviceTypeIndex(slopeData),
        [slopeData]
    );
    const getDeviceType = useCallback(
        (alert) =>
            alert?.device_type ||
            alert?.deviceType ||
            deviceTypeIndex.get(alert?.device_name) ||
            extractDeviceType(alert?.device_name),
        [deviceTypeIndex]
    );
    const getDeviceTypeRank = useCallback(
        (type) => {
            const idx = DEVICE_TYPE_ORDER.indexOf(type);
            return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
        },
        []
    );
    const districtOrder = useMemo(() => {
        const seen = new Set();
        const order = [];
        for (const alert of alertData) {
            const district = getAlertDistrict(alert);
            if (!seen.has(district)) {
                seen.add(district);
                order.push(district);
            }
        }
        return order;
    }, [alertData]);
    const districtRank = useCallback(
        (district) => {
            const idx = districtOrder.indexOf(district);
            return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
        },
        [districtOrder]
    );
    const roleValue = user?.role || user?.role_name || user?.roleName || '';
    const roleText = roleValue ? roleValue.toString() : '';
    const roleLower = roleText.toLowerCase();
    const canViewOfflineSection =
        roleLower.includes('admin') || roleText.includes('陸島人員');
    const groupByDistrict = useCallback(
        (items, getAlert) => {
            const byDistrict = new Map();
            for (const item of items) {
                const alert = getAlert(item);
                const district = getAlertDistrict(alert);
                if (!byDistrict.has(district)) {
                    byDistrict.set(district, []);
                }
                byDistrict.get(district).push(item);
            }
            return Array.from(byDistrict.entries())
                .sort(([districtA], [districtB]) => {
                    const rankA = districtRank(districtA);
                    const rankB = districtRank(districtB);
                    if (rankA !== rankB) return rankA - rankB;
                    return districtA.localeCompare(districtB);
                })
                .map(([district, entries]) => ({
                    district,
                    entries,
                }));
        },
        [districtRank]
    );
    const renderGroupedBlocks = useCallback(
        (items, getAlert, renderCard) => {
            const groups = groupByDistrict(items, getAlert);
            return groups.map((group) => (
                <div key={group.district} style={styles.districtGroup}>
                    <div style={styles.districtHeader}>
                        <span style={styles.districtTitle}>{group.district}</span>
                    </div>
                    <div style={styles.grid}>
                        {group.entries
                            .slice()
                            .sort((a, b) => {
                                const alertA = getAlert(a);
                                const alertB = getAlert(b);
                                const typeA = getDeviceType(alertA) || '';
                                const typeB = getDeviceType(alertB) || '';
                                const rankA = getDeviceTypeRank(typeA);
                                const rankB = getDeviceTypeRank(typeB);
                                if (rankA !== rankB) return rankA - rankB;
                                return typeA.localeCompare(typeB);
                            })
                            .map((entry) => renderCard(entry))}
                    </div>
                </div>
            ));
        },
        [getDeviceType, getDeviceTypeRank, groupByDistrict, styles]
    );

    const { groupedAlerts, disabledAlerts } = useMemo(() => {
        const grouped = { yellow: [], red: [], gray: [] };
        const disabledMap = new Map();
        const mergedByStatus = {
            gray: new Map(),
        };
        if (!Array.isArray(alertData)) {
            return { groupedAlerts: grouped, disabledAlerts: [] };
        }
        for (const alert of alertData) {
            if (!alert) continue;
            const disableInfo = getDisableInfo(alert, { ignoreXml });
            if (disableInfo.thresholdDisabled || disableInfo.xmlDisabled) {
                const key = getDeviceMergeKey(alert);
                if (key) {
                    const prev = disabledMap.get(key);
                    const nextAlert = stripAxisAlert(alert);
                    const mergedAlert = prev
                        ? pickLatestAlert(prev.alert, nextAlert)
                        : nextAlert;
                    const mergedInfo = prev
                        ? mergeDisableInfo(prev.disableInfo, disableInfo)
                        : disableInfo;
                    disabledMap.set(key, { alert: mergedAlert, disableInfo: mergedInfo });
                } else {
                    disabledMap.set(
                        Symbol('disabled'),
                        { alert: stripAxisAlert(alert), disableInfo }
                    );
                }
                if (disableInfo.thresholdDisabled) {
                    continue;
                }
            }

            const status = alert.status;
            if (!grouped[status]) continue;

            if (status === 'yellow' || status === 'red') {
                grouped[status].push(...expandAxisAlerts([alert]));
                continue;
            }

            const key = getDeviceMergeKey(alert);
            if (!key) {
                grouped[status].push(stripAxisAlert(alert));
                continue;
            }
            const map = mergedByStatus[status] ?? new Map();
            const nextAlert = stripAxisAlert(alert);
            const prev = map.get(key);
            map.set(key, prev ? pickLatestAlert(prev, nextAlert) : nextAlert);
            mergedByStatus[status] = map;
        }

        Object.entries(mergedByStatus).forEach(([status, map]) => {
            if (!grouped[status]) return;
            grouped[status].push(...map.values());
        });

        return {
            groupedAlerts: grouped,
            disabledAlerts: Array.from(disabledMap.values()),
        };
    }, [alertData, ignoreXml]);
    const hasAnyVisibleCards = useMemo(() => {
        const yellowCount = groupedAlerts?.yellow?.length || 0;
        const redCount = groupedAlerts?.red?.length || 0;
        const grayCount = canViewOfflineSection
            ? groupedAlerts?.gray?.length || 0
            : 0;
        const disabledCount = canViewOfflineSection ? disabledAlerts.length : 0;
        return yellowCount + redCount + grayCount + disabledCount > 0;
    }, [groupedAlerts, disabledAlerts, canViewOfflineSection]);

    const handleNavigate = (deviceName) => {
        if (!deviceName) return;
        navigate(`/device/${deviceName}`);
    };

    const renderActiveCard = (alert) => {
        const meta = PRESENT[alert.status] ?? PRESENT.gray;
        const lastUpdate = alert.measurement_obs_time
            ? fmtDate(alert.measurement_obs_time)
            : null;
        const relativeUpdate = alert.measurement_obs_time
            ? timeAgo(alert.measurement_obs_time)
            : null;
        const cardKey = getAlertKey(alert);
        const deviceType = getDeviceType(alert);
        const TypeIcon = deviceTypeIcons[deviceType] || null;
        const axisLabel =
            alert.status === 'yellow' || alert.status === 'red'
                ? getAxisLabel(alert?.axis)
                : '';

        return (
            <article
                key={`active-${cardKey}`}
                style={{
                    ...styles.card,
                    background: meta.bg,
                    boxShadow: `0 0 0 1px ${meta.ring} inset, 0 10px 24px rgba(15,23,42,0.08)`,
                }}
                title={`${meta.label}`}
                onClick={() => handleNavigate(alert.device_name)}
            >
                <div style={styles.cardTop}>
                    <div style={styles.title}>
                        {/* <div style={styles.subtle}>Device</div> */}
                        <div style={styles.nameRow}>
                            {TypeIcon && (
                                <img
                                    src={TypeIcon}
                                    alt={deviceType || 'device type'}
                                    style={styles.nameIcon}
                                />
                            )}
                            <div style={styles.name}>{alert.device_name}</div>
                            {axisLabel ? (
                                <span style={styles.axisTag}>{axisLabel}</span>
                            ) : null}
                        </div>
                    </div>
                    <span
                        style={{
                            ...styles.badge,
                            color: meta.text,
                            borderColor: meta.ring,
                        }}
                    >
                        {meta.dot} {meta.label}
                    </span>
                </div>

                <div style={styles.context} aria-live="polite">
                    {alert.context}
                </div>

                <div style={styles.meta}>
                    <div style={styles.metaLine}>
                        <span style={styles.metaLabel}>最新資料：</span>
                        <span style={!lastUpdate ? styles.placeholder : undefined}>
                            {lastUpdate || '—'}
                        </span>
                        {relativeUpdate && (
                            <span style={styles.metaRelative}> · {relativeUpdate}</span>
                        )}
                    </div>
                </div>
            </article>
        );
    };

    const renderDisabledCard = ({ alert, disableInfo }) => {
        const lastUpdate = alert.measurement_obs_time
            ? fmtDate(alert.measurement_obs_time)
            : null;
        const relativeUpdate = alert.measurement_obs_time
            ? timeAgo(alert.measurement_obs_time)
            : null;
        const cardKey = getAlertKey(alert);
        const reasonText = buildDisabledReason(disableInfo);
        const deviceType = getDeviceType(alert);
        const TypeIcon = deviceTypeIcons[deviceType] || null;
        const axisLabel =
            alert.status === 'yellow' || alert.status === 'red'
                ? getAxisLabel(alert?.axis)
                : '';

        return (
            <article
                key={`disabled-${cardKey}`}
                style={{
                    ...styles.card,
                    background: '#eef2ff',
                    boxShadow: '0 0 0 1px #c7d2fe inset, 0 10px 20px rgba(15,23,42,0.06)',
                }}
                title={reasonText}
                onClick={() => handleNavigate(alert.device_name)}
            >
                <div style={styles.cardTop}>
                    <div style={styles.title}>
                        {/* <div style={styles.subtle}>Device</div> */}
                        <div style={styles.nameRow}>
                            {TypeIcon && (
                                <img
                                    src={TypeIcon}
                                    alt={deviceType || 'device type'}
                                    style={styles.nameIcon}
                                />
                            )}
                            <div style={styles.name}>{alert.device_name}</div>
                            {axisLabel ? (
                                <span style={styles.axisTag}>{axisLabel}</span>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div style={styles.disabledTag}>{reasonText}</div>

                <div style={styles.context}>{alert.context}</div>

                <div style={styles.meta}>
                    <div style={styles.metaLine}>
                        <span style={styles.metaLabel}>最新資料：</span>
                        <span style={!lastUpdate ? styles.placeholder : undefined}>
                            {lastUpdate || '—'}
                        </span>
                        {relativeUpdate && (
                            <span style={styles.metaRelative}> · {relativeUpdate}</span>
                        )}
                    </div>
                </div>
            </article>
        );
    };

    return (
        <div style={styles.wrapper}>
            {!hasAnyVisibleCards && (
                <section style={styles.section}>
                    <div style={styles.sectionEmpty}>
                        <p>目前無警戒狀態</p>
                    </div>
                </section>
            )}
            {SECTION_CONFIG.map((section) => {
                if (section.key === 'gray' && !canViewOfflineSection) return null;
                const sectionAlerts = groupedAlerts[section.key] || [];
                if (!sectionAlerts.length) return null;
                return (
                    <section key={section.key} style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <div style={styles.sectionTitleWrap}>
                                <h2 style={styles.sectionTitle}>{section.title}</h2>
                                <span style={styles.sectionCount}>
                                    {sectionAlerts.length}
                                </span>
                            </div>
                            <p style={styles.sectionSubtitle}>{section.description}</p>
                        </div>
                        {renderGroupedBlocks(
                            sectionAlerts,
                            (alert) => alert,
                            renderActiveCard
                        )}
                    </section>
                );
            })}

            {canViewOfflineSection && (
                <section style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <div style={styles.sectionTitleWrap}>
                            <h2 style={styles.sectionTitle}>
                                {ignoreXml
                                    ? DISABLED_SECTION_ZOO.title
                                    : DISABLED_SECTION_DEFAULT.title}
                            </h2>
                            <span style={styles.sectionCount}>{disabledAlerts.length}</span>
                        </div>
                        <p style={styles.sectionSubtitle}>
                            {ignoreXml
                                ? DISABLED_SECTION_ZOO.description
                                : DISABLED_SECTION_DEFAULT.description}
                        </p>
                    </div>
                    {disabledAlerts.length ? (
                        renderGroupedBlocks(
                            disabledAlerts,
                            (entry) => entry.alert,
                            renderDisabledCard
                        )
                    ) : (
                        <div style={styles.sectionEmpty}>
                            <p>所有設備皆啟用警戒偵測</p>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}

function getAlertKey(alert) {
    const axisLabel =
        alert?.status === 'yellow' || alert?.status === 'red'
            ? getAxisLabel(alert?.axis)
            : '';
    const baseKey =
        alert?.id ??
        alert?.device_id ??
        alert?.device_name ??
        alert?.context ??
        alert?.start_time ??
        'unknown';
    return axisLabel ? `${baseKey}-${axisLabel}` : baseKey;
}

function getDeviceMergeKey(alert) {
    return (
        alert?.device_id ||
        alert?.device_name ||
        alert?.id ||
        alert?.context ||
        null
    );
}

function stripAxisAlert(alert) {
    return {
        ...alert,
        axis: undefined,
        Axis: undefined,
        axis_name: undefined,
    };
}

function mergeDisableInfo(prev, next) {
    return {
        isDisabled: prev.isDisabled || next.isDisabled,
        thresholdDisabled: prev.thresholdDisabled || next.thresholdDisabled,
        xmlDisabled: prev.xmlDisabled || next.xmlDisabled,
    };
}

function pickLatestAlert(current, next) {
    const currentTime = parseMeasurementTime(
        current?.measurement_obs_time || current?.start_time
    );
    const nextTime = parseMeasurementTime(
        next?.measurement_obs_time || next?.start_time
    );
    if (nextTime?.isValid && !currentTime?.isValid) return next;
    if (nextTime?.isValid && currentTime?.isValid) {
        return nextTime.toMillis() > currentTime.toMillis() ? next : current;
    }
    return current || next;
}

function buildDeviceTypeIndex(slopeData) {
    const index = new Map();
    if (!slopeData || typeof slopeData !== 'object') return index;

    const pools = {
        ALC: slopeData.force,
        OW: slopeData.water_level,
        TI: slopeData.tilt,
        RA: slopeData.ra,
        SIS: slopeData.sis,
    };

    for (const [type, list] of Object.entries(pools)) {
        if (!Array.isArray(list)) continue;
        for (const item of list) {
            if (item?.name) {
                index.set(item.name, type);
            }
        }
    }

    return index;
}

function parseMeasurementTime(value) {
    if (!value) return null;
    if (value instanceof Date) return DateTime.fromJSDate(value);
    if (typeof value === 'number') {
        const ms = value < 1e12 ? value * 1000 : value;
        return DateTime.fromMillis(ms);
    }
    const str = String(value).trim();
    const iso = DateTime.fromISO(str, { zone: 'Asia/Taipei' });
    if (iso.isValid) return iso;
    const dashed = DateTime.fromFormat(str, 'yyyy-MM-dd HH:mm', {
        zone: 'Asia/Taipei',
    });
    if (dashed.isValid) return dashed;
    const fallback = DateTime.fromFormat(str, 'yyyy/MM/dd H:mm', {
        zone: 'Asia/Taipei',
    });
    if (fallback.isValid) return fallback;
    return DateTime.fromJSDate(new Date(str));
}

function fmtDate(value) {
    const dt = parseMeasurementTime(value);
    if (!dt || !dt.isValid) return '—';
    return dt.setZone('Asia/Taipei').toFormat('yyyy/MM/dd HH:mm');
}

function timeAgo(value) {
    const dt = parseMeasurementTime(value);
    if (!dt || !dt.isValid) return '';
    const diff = Date.now() - dt.toMillis();
    const mins = Math.trunc(diff / 60000);
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    if (Math.abs(mins) < 60) return rtf.format(-mins, 'minute');
    const hrs = Math.trunc(diff / 3600000);
    if (Math.abs(hrs) < 24) return rtf.format(-hrs, 'hour');
    const days = Math.trunc(diff / 86400000);
    return rtf.format(-days, 'day');
}

const createStyles = (isMobile) => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 16 : 24,
        color: '#0f172a',
        background: '#f5f7fb',
        minHeight: '100vh',
        padding: isMobile ? 12 : 24,
        boxSizing: 'border-box',
    },
    section: {
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: 20,
        padding: isMobile ? 16 : 20,
        boxShadow: '0 15px 35px rgba(15,23,42,0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    sectionHeader: {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    sectionTitleWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    sectionTitle: {
        fontSize: isMobile ? 18 : 20,
        fontWeight: 600,
        margin: 0,
        color: '#0f172a',
    },
    sectionCount: {
        fontSize: 12,
        fontWeight: 600,
        padding: '2px 10px',
        background: '#e2e8f0',
        borderRadius: 999,
        color: '#0f172a',
    },
    sectionSubtitle: {
        color: '#64748b',
        fontSize: 13,
        margin: 0,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: isMobile ? 10 : 12,
    },
    districtGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        paddingTop: 4,
    },
    districtHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        paddingBottom: 4,
        borderBottom: '1px solid #e2e8f0',
    },
    districtTitle: {
        fontSize: isMobile ? 15 : 16,
        fontWeight: 700,
        color: '#0f172a',
    },
    sectionEmpty: {
        border: '1px dashed #cbd5f5',
        borderRadius: 16,
        padding: 20,
        textAlign: 'center',
        color: '#94a3b8',
        background: '#f8fafc',
    },
    card: {
        borderRadius: 18,
        padding: isMobile ? 8 : 12,
        minHeight: isMobile ? 98 : 108,
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 4 : 6,
        cursor: 'pointer',
        transition: 'transform .15s ease, box-shadow .15s ease',
    },
    cardTop: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
    },
    title: { minWidth: 0, flex: 1 },
    subtle: { color: '#94a3b8', fontSize: 12, marginBottom: 2 },
    nameRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 4,
    },
    axisTag: {
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: 999,
        background: '#e2e8f0',
        color: '#334155',
    },
    nameIcon: {
        width: 20,
        height: 20,
        flexShrink: 0,
    },
    name: {
        fontWeight: 700,
        fontSize: isMobile ? 14 : 18,
        lineHeight: 1.15,
        wordBreak: 'break-word',
    },
    badge: {
        padding: isMobile ? '4px 8px' : '6px 10px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        fontSize: isMobile ? 11 : 12,
        fontWeight: 700,
        background: 'rgba(255,255,255,0.7)',
        whiteSpace: 'nowrap',
    },
    context: {
        minHeight: isMobile ? 26 : 38,
        display: 'flex',
        alignItems: 'center',
        color: '#334155',
        fontSize: isMobile ? 11 : 14,
        lineHeight: 1.3,
    },
    meta: {
        fontSize: isMobile ? 11 : 12,
        color: '#475569',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 2 : 4,
        minHeight: isMobile ? 28 : 34,
    },
    metaLine: { display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'baseline' },
    metaLabel: { color: '#94a3b8' },
    metaRelative: { color: '#94a3b8', fontSize: 11 },
    disabledTag: {
        alignSelf: 'flex-start',
        padding: '4px 10px',
        borderRadius: 999,
        background: '#c7d2fe',
        color: '#312e81',
        fontSize: 12,
        fontWeight: 600,
    },
    placeholder: {
        color: '#cbd5f5',
    },
});

function useMediaQuery(query) {
    const getMatch = () =>
        typeof window !== 'undefined' ? window.matchMedia(query).matches : false;
    const [matches, setMatches] = useState(getMatch);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const media = window.matchMedia(query);
        const listener = () => setMatches(media.matches);
        listener();
        media.addEventListener('change', listener);
        return () => media.removeEventListener('change', listener);
    }, [query]);

    return matches;
}
